<template>
  <div class="relative md:overflow-hidden">
    <Slider
      id="product-header-gallery"
      ref="slider"
      class="h-full product-header-gallery-slider aspect-1"
      :pagination="media.length > 1"
      :index="modelValue"
      :model-value="modelValue"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <div
        v-for="(item, i) in media"
        :key="'product-media-' + i"
        class="size-full flex items-center justify-center"
        v-bind="getItemAttributes(i)"
        @click="openLightbox(i)"
      >
        <MediaImage
          v-if="item.__typename === 'MediaImage'"
          v-bind="item"
          :image-style="imageStyle"
          class="cursor-zoom-in"
        />
        <ProductSliderVideo
          v-else-if="item.__typename === 'MediaVideo'"
          :video="item"
        />
      </div>
    </Slider>
  </div>
</template>

<script lang="ts" setup>
import type { ProductVariationFragment } from '#graphql-operations'
import type { LightboxMediaItem } from '~/composables/useLightbox'
import type Slider from '~/components/Slider/index.vue'
import { defineExpedImageStyle } from '~~/utils/defineExpedImageStyle'

type ProductMedia = NonNullable<ProductVariationFragment['images']>[number]

const props = withDefaults(
  defineProps<{
    media?: ProductMedia[]
    modelValue: number
  }>(),
  {
    media: () => [],
  },
)

defineEmits(['update:modelValue'])

const slider = ref<InstanceType<typeof Slider> | null>(null)

const { openLightbox, getItemAttributes } = useLightbox(
  'product-header-gallery',
  props.media as LightboxMediaItem[],
  {
    slider,
  },
)

const imageStyle = defineExpedImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  cropType: 'contain',
  sizes: {
    sm: 400,
    md: 500,
    lg: 600,
    xl: 700,
    '2xl': 933,
  },
})
</script>

<style lang="postcss">
.product-header-gallery-slider {
  .flicking-viewport {
    @apply h-full;
  }
}

.pswp {
  --pswp-bg: white;
}

.pswp__bullet {
  @apply w-[12px] h-[12px] rounded-full bg-white ring-black ring-1 cursor-pointer;
  &:not(.pswp__bullet--active):hover {
    @apply bg-gray;
  }
}

.pswp__bullet--active {
  @apply bg-black;
}

.pswp__button--arrow--prev,
.pswp__button--arrow--next {
  @apply flex items-center;
  &:hover > div {
    @apply bg-gray;
  }
  > div {
    @apply w-40 h-40 bg-white text-black flex items-center justify-center rounded-full;
    svg {
      @apply h-20 w-10;
    }
  }
}
.pswp__button--arrow--prev > div {
  @apply ml-30;
  svg {
    @apply -translate-x-1;
  }
}
.pswp__button--arrow--next {
  @apply justify-end;
  > div {
    @apply mr-30;
  }
  svg {
    @apply translate-x-1;
  }
}

.pswp__bullets-indicator {
  @apply flex gap-[8px] px-20 bg-white/60 h-50 items-center;
}

.pswp__custom-caption {
  @apply px-20 py-10 min-h-[50px] flex items-end xl:items-center xl:flex-1 mt-auto;
  background: white;
  font-size: 16px;
  color: black;
}
.pswp__img {
  @apply object-contain;
}

.pswp__scroll-wrap {
  @apply flex flex-col-reverse items-start xl:flex-row xl:items-end xl:justify-start;
}

.pswp-video {
  @apply flex items-center justify-center;
  width: 100vw;
  height: 100vh;

  video {
    aspect-ratio: 1 / 1;
    max-width: 100vh;
    max-height: 100vh;
    width: 100%;
    height: auto;
  }
}

.product-fade-enter-active,
.product-fade-leave-active {
  transition: 0.3s cubic-bezier(0.43, 0.72, 0.56, 0.99);
  @apply absolute top-0 left-0 w-full h-full;
}
.product-fade-enter-from,
.product-fade-leave-to {
  opacity: 0;
}
</style>
